/*
=========================================================
Código hecho por Rcrea
JS: Retail Hero Fullscreen Carousel

Actualización:
- Swipe mobile
- Layout dinámico mobile
- Dots nunca chocan con CTA
- Hero aumenta altura cuando es necesario
- Shop The Look mobile por tap
- Shop The Look desktop por click
- Primer tap mobile abre
- Segundo tap mobile navega al producto
=========================================================
*/

(() => {

  class RcreaRetailHero {

    constructor(section) {

      this.section = section;

      this.slides = Array.from(
        section.querySelectorAll(
          "[data-rcrea-slide]"
        )
      );

      this.dots = Array.from(
        section.querySelectorAll(
          "[data-rcrea-dot]"
        )
      );

      this.productBadges = Array.from(
        section.querySelectorAll(
          "[data-rcrea-product-badge]"
        )
      );

      this.controls =
        section.querySelector(
          "[data-rcrea-controls]"
        );

      this.prevButton =
        section.querySelector(
          "[data-rcrea-prev]"
        );

      this.nextButton =
        section.querySelector(
          "[data-rcrea-next]"
        );


      /* =====================================================
         CAROUSEL
      ===================================================== */

      this.activeIndex = 0;

      this.timer = null;

      this.isPaused = false;

      this.autoplay =
        section.dataset.autoplay ===
        "true";

      this.pauseOnHover =
        section.dataset.pauseHover ===
        "true";

      this.interval =
        Number(
          section.dataset.interval
        ) || 6000;

      this.reducedMotion =
        window
          .matchMedia(
            "(prefers-reduced-motion: reduce)"
          )
          .matches;


      /* =====================================================
         SWIPE
      ===================================================== */

      this.touchStartX = null;

      this.touchStartY = null;

      this.touchStartTime = null;

      this.swipeThreshold = 45;

      this.swipeMaxDuration = 800;


      /* =====================================================
         LAYOUT
      ===================================================== */

      this.layoutRAF = null;

      this.resizeTimer = null;


      /* =====================================================
         BIND
      ===================================================== */

      this.handleTouchStart =
        this.handleTouchStart.bind(this);

      this.handleTouchEnd =
        this.handleTouchEnd.bind(this);

      this.handleTouchCancel =
        this.handleTouchCancel.bind(this);

      this.handleDocumentClick =
        this.handleDocumentClick.bind(this);

      this.handleResize =
        this.handleResize.bind(this);


      this.section.style.setProperty(
        "--rcrea-slide-duration",
        `${this.interval}ms`
      );


      if (!this.slides.length) {
        return;
      }


      this.bindEvents();

      this.prepareMedia();

      this.prepareProductBadges();

      this.goTo(
        0,
        false
      );


      if (
        this.autoplay &&
        this.slides.length > 1 &&
        !this.reducedMotion
      ) {

        this.start();

      }


      /*
      Recalcular después de cargar webfonts.
      Muy importante para Lexend Mega.
      */

      if (
        document.fonts &&
        document.fonts.ready
      ) {

        document.fonts.ready.then(
          () => {

            this.scheduleMobileLayout();

          }
        );

      }


      window.setTimeout(
        () => {

          this.scheduleMobileLayout();

        },
        100
      );

    }


    /* =====================================================
       EVENTS
    ====================================================== */

    bindEvents() {

      /* DOTS */

      this.dots.forEach(
        (dot) => {

          dot.addEventListener(
            "click",
            () => {

              const index =
                Number(
                  dot.dataset.index
                );


              this.goTo(
                index,
                true
              );

            }
          );

        }
      );


      /* ARROWS */

      if (this.prevButton) {

        this.prevButton.addEventListener(
          "click",
          () => {

            this.prev();

          }
        );

      }


      if (this.nextButton) {

        this.nextButton.addEventListener(
          "click",
          () => {

            this.next();

          }
        );

      }


      /* SWIPE */

      this.section.addEventListener(
        "touchstart",
        this.handleTouchStart,
        {
          passive: true
        }
      );


      this.section.addEventListener(
        "touchend",
        this.handleTouchEnd,
        {
          passive: true
        }
      );


      this.section.addEventListener(
        "touchcancel",
        this.handleTouchCancel,
        {
          passive: true
        }
      );


      /* DESKTOP HOVER */

      if (this.pauseOnHover) {

        this.section.addEventListener(
          "mouseenter",
          () => this.pause()
        );


        this.section.addEventListener(
          "mouseleave",
          () => this.resume()
        );

      }


      /* FOCUS */

      this.section.addEventListener(
        "focusin",
        () => this.pause()
      );


      this.section.addEventListener(
        "focusout",
        (event) => {

          if (
            !this.section.contains(
              event.relatedTarget
            )
          ) {

            this.resume();

          }

        }
      );


      /* VISIBILITY */

      document.addEventListener(
        "visibilitychange",
        () => {

          if (document.hidden) {

            this.pause();

          } else {

            this.resume();

          }

        }
      );


      /* KEYBOARD */

      this.section.addEventListener(
        "keydown",
        (event) => {

          if (
            event.key ===
            "ArrowLeft"
          ) {

            this.prev();

          }


          if (
            event.key ===
            "ArrowRight"
          ) {

            this.next();

          }


          if (
            event.key ===
            "Escape"
          ) {

            this.closeProductBadges();

          }

        }
      );


      /* CLICK GLOBAL */

      document.addEventListener(
        "click",
        this.handleDocumentClick
      );


      /* RESIZE */

      window.addEventListener(
        "resize",
        this.handleResize
      );

    }


    /* =====================================================
       MOBILE DYNAMIC LAYOUT
    ====================================================== */

    scheduleMobileLayout() {

      if (this.layoutRAF) {

        window.cancelAnimationFrame(
          this.layoutRAF
        );

      }


      this.layoutRAF =
        window.requestAnimationFrame(
          () => {

            this.updateMobileLayout();

          }
        );

    }


    updateMobileLayout() {

      const isMobile =
        window.matchMedia(
          "(max-width: 749px)"
        ).matches;


      if (!isMobile) {

        this.section.style.removeProperty(
          "--rcrea-mobile-runtime-height"
        );

        this.section.style.removeProperty(
          "--rcrea-mobile-runtime-media-height"
        );

        this.section.style.removeProperty(
          "--rcrea-mobile-controls-top"
        );

        return;

      }


      const styles =
        window.getComputedStyle(
          this.section
        );


      const baseHeight =
        parseFloat(
          styles.getPropertyValue(
            "--rcrea-mobile-hero-height"
          )
        ) || 760;


      const mediaPercentage =
        parseFloat(
          styles.getPropertyValue(
            "--rcrea-mobile-media-height"
          )
        ) || 40;


      const controlsGap =
        parseFloat(
          styles.getPropertyValue(
            "--rcrea-mobile-controls-gap"
          )
        ) || 16;


      /*
      La imagen se calcula sobre la altura
      configurada, no sobre la altura dinámica.

      De esta forma, si aparecen dos CTA,
      aumenta solamente el bloque inferior.
      */

      const mediaHeight =
        baseHeight *
        (
          mediaPercentage /
          100
        );


      this.section.style.setProperty(
        "--rcrea-mobile-runtime-media-height",
        `${mediaHeight}px`
      );


      /*
      Reset temporal a altura base para medir.
      */

      this.section.style.setProperty(
        "--rcrea-mobile-runtime-height",
        `${baseHeight}px`
      );


      window.requestAnimationFrame(
        () => {

          const activeSlide =
            this.slides[
              this.activeIndex
            ];


          if (!activeSlide) {
            return;
          }


          const actions =
            activeSlide.querySelector(
              ".rcrea-retail-hero__actions"
            );


          if (!actions) {
            return;
          }


          const sectionRect =
            this.section
              .getBoundingClientRect();


          const actionsRect =
            actions
              .getBoundingClientRect();


          /*
          Posición vertical exacta donde
          terminan los CTA.
          */

          const actionsBottom =
            actionsRect.bottom -
            sectionRect.top;


          /*
          Los dots siempre empiezan después
          del último CTA.
          */

          const controlsTop =
            actionsBottom +
            controlsGap;


          this.section.style.setProperty(
            "--rcrea-mobile-controls-top",
            `${controlsTop}px`
          );


          const controlsHeight =
            this.controls
              ? this.controls.offsetHeight
              : 30;


          /*
          Espacio inferior después de dots.
          */

          const bottomSafeSpace = 24;


          const requiredHeight =
            controlsTop +
            controlsHeight +
            bottomSafeSpace;


          /*
          Nunca disminuye por debajo
          de la altura elegida en Shopify.
          */

          const finalHeight =
            Math.max(
              baseHeight,
              requiredHeight
            );


          this.section.style.setProperty(
            "--rcrea-mobile-runtime-height",
            `${finalHeight}px`
          );

        }
      );

    }


    handleResize() {

      window.clearTimeout(
        this.resizeTimer
      );


      this.resizeTimer =
        window.setTimeout(
          () => {

            this.scheduleMobileLayout();

          },
          100
        );

    }


    /* =====================================================
       SWIPE
    ====================================================== */

    handleTouchStart(event) {

      if (
        event.touches.length !== 1
      ) {

        this.resetSwipe();

        return;

      }


      /*
      No iniciar swipe desde elementos
      interactivos.
      */

      const interactiveElement =
        event.target.closest(
          "a, button, input, textarea, select"
        );


      if (interactiveElement) {

        this.resetSwipe();

        return;

      }


      const touch =
        event.touches[0];


      this.touchStartX =
        touch.clientX;


      this.touchStartY =
        touch.clientY;


      this.touchStartTime =
        Date.now();

    }


    handleTouchEnd(event) {

      if (
        this.touchStartX === null ||
        this.touchStartY === null
      ) {

        return;

      }


      const touch =
        event.changedTouches[0];


      if (!touch) {

        this.resetSwipe();

        return;

      }


      const deltaX =
        touch.clientX -
        this.touchStartX;


      const deltaY =
        touch.clientY -
        this.touchStartY;


      const duration =
        Date.now() -
        this.touchStartTime;


      const absX =
        Math.abs(deltaX);


      const absY =
        Math.abs(deltaY);


      const isValidSwipe =
        absX >=
          this.swipeThreshold &&

        absX >
          absY * 1.15 &&

        duration <=
          this.swipeMaxDuration;


      if (isValidSwipe) {

        if (deltaX < 0) {

          this.next();

        }


        if (deltaX > 0) {

          this.prev();

        }

      }


      this.resetSwipe();

    }


    handleTouchCancel() {

      this.resetSwipe();

    }


    resetSwipe() {

      this.touchStartX = null;

      this.touchStartY = null;

      this.touchStartTime = null;

    }


    /* =====================================================
       SHOP THE LOOK
    ====================================================== */

    prepareProductBadges() {

      this.productBadges.forEach(
        (badge) => {

          badge.setAttribute(
            "aria-expanded",
            "false"
          );


          badge.addEventListener(
            "click",
            (event) => {

              event.stopPropagation();


              const productUrl =
                badge.dataset.productUrl;


              const isMobile =
                window.matchMedia(
                  "(max-width: 749px)"
                ).matches;


              /*
              DESKTOP

              Clic → producto.
              Hover sigue mostrando información.
              */

              if (!isMobile) {

                if (productUrl) {

                  window.location.href =
                    productUrl;

                }

                return;

              }


              /*
              MOBILE

              Primer tap:
              abre Shop The Look.

              Segundo tap:
              abre producto.
              */

              const isOpen =
                badge.classList.contains(
                  "is-open"
                );


              if (!isOpen) {

                this.closeProductBadges(
                  badge
                );


                badge.classList.add(
                  "is-open"
                );


                badge.setAttribute(
                  "aria-expanded",
                  "true"
                );


                return;

              }


              if (productUrl) {

                window.location.href =
                  productUrl;

                return;

              }


              badge.classList.remove(
                "is-open"
              );


              badge.setAttribute(
                "aria-expanded",
                "false"
              );

            }
          );

        }
      );

    }


    closeProductBadges(
      exceptBadge = null
    ) {

      this.productBadges.forEach(
        (badge) => {

          if (
            exceptBadge &&
            badge === exceptBadge
          ) {

            return;

          }


          badge.classList.remove(
            "is-open"
          );


          badge.setAttribute(
            "aria-expanded",
            "false"
          );

        }
      );

    }


    handleDocumentClick(event) {

      if (
        !this.section.contains(
          event.target
        )
      ) {

        this.closeProductBadges();

        return;

      }


      const clickedBadge =
        event.target.closest(
          "[data-rcrea-product-badge]"
        );


      if (!clickedBadge) {

        this.closeProductBadges();

      }

    }


    /* =====================================================
       VIDEO
    ====================================================== */

    prepareMedia() {

      this.slides.forEach(
        (
          slide,
          index
        ) => {

          const video =
            slide.querySelector(
              "video"
            );


          if (!video) {
            return;
          }


          video.muted = true;

          video.playsInline = true;


          if (
            index ===
            this.activeIndex
          ) {

            this.safePlay(
              video
            );

          } else {

            video.pause();

          }

        }
      );

    }


    /* =====================================================
       GO TO
    ====================================================== */

    goTo(
      index,
      userAction = false
    ) {

      if (
        index < 0 ||
        index >=
          this.slides.length
      ) {

        return;

      }


      this.closeProductBadges();


      this.activeIndex =
        index;


      this.slides.forEach(
        (
          slide,
          slideIndex
        ) => {

          const isActive =
            slideIndex ===
            index;


          const video =
            slide.querySelector(
              "video"
            );


          slide.classList.toggle(
            "is-active",
            isActive
          );


          if (video) {

            if (isActive) {

              video.currentTime = 0;


              this.safePlay(
                video
              );

            } else {

              video.pause();

            }

          }

        }
      );


      this.dots.forEach(
        (
          dot,
          dotIndex
        ) => {

          const isActive =
            dotIndex ===
            index;


          const progress =
            dot.querySelector(
              ".rcrea-retail-hero__dot-progress"
            );


          dot.classList.toggle(
            "is-active",
            isActive
          );


          dot.setAttribute(
            "aria-selected",
            isActive
              ? "true"
              : "false"
          );


          if (progress) {

            progress.style.animation =
              "none";


            void progress.offsetWidth;


            if (
              isActive &&
              this.autoplay &&
              !this.reducedMotion
            ) {

              progress.style.animation =
                "";

            }

          }

        }
      );


      /*
      Recalcula posición dots según contenido
      del nuevo slide.
      */

      this.scheduleMobileLayout();


      if (userAction) {

        this.restart();

      }

    }


    /* =====================================================
       NEXT
    ====================================================== */

    next() {

      const nextIndex =
        (
          this.activeIndex +
          1
        ) %
        this.slides.length;


      this.goTo(
        nextIndex,
        true
      );

    }


    /* =====================================================
       PREVIOUS
    ====================================================== */

    prev() {

      const prevIndex =
        (
          this.activeIndex -
          1 +
          this.slides.length
        ) %
        this.slides.length;


      this.goTo(
        prevIndex,
        true
      );

    }


    /* =====================================================
       AUTOPLAY
    ====================================================== */

    start() {

      this.stop();


      if (
        !this.autoplay ||
        this.slides.length <= 1 ||
        this.reducedMotion
      ) {

        return;

      }


      this.timer =
        window.setInterval(
          () => {

            if (!this.isPaused) {

              const nextIndex =
                (
                  this.activeIndex +
                  1
                ) %
                this.slides.length;


              this.goTo(
                nextIndex,
                false
              );

            }

          },
          this.interval
        );

    }


    stop() {

      if (!this.timer) {
        return;
      }


      window.clearInterval(
        this.timer
      );


      this.timer = null;

    }


    restart() {

      if (
        !this.autoplay ||
        this.reducedMotion
      ) {

        return;

      }


      this.start();

    }


    /* =====================================================
       PAUSE
    ====================================================== */

    pause() {

      if (!this.autoplay) {
        return;
      }


      this.isPaused = true;


      this.section.classList.add(
        "is-paused"
      );


      const activeVideo =
        this.slides[
          this.activeIndex
        ]?.querySelector(
          "video"
        );


      if (activeVideo) {

        activeVideo.pause();

      }

    }


    /* =====================================================
       RESUME
    ====================================================== */

    resume() {

      if (!this.autoplay) {
        return;
      }


      this.isPaused = false;


      this.section.classList.remove(
        "is-paused"
      );


      const activeVideo =
        this.slides[
          this.activeIndex
        ]?.querySelector(
          "video"
        );


      if (activeVideo) {

        this.safePlay(
          activeVideo
        );

      }

    }


    safePlay(video) {

      const playPromise =
        video.play();


      if (
        playPromise !==
        undefined
      ) {

        playPromise.catch(
          () => {}
        );

      }

    }


    /* =====================================================
       DESTROY
    ====================================================== */

    destroy() {

      this.stop();


      if (this.layoutRAF) {

        window.cancelAnimationFrame(
          this.layoutRAF
        );

      }


      window.clearTimeout(
        this.resizeTimer
      );


      this.section.removeEventListener(
        "touchstart",
        this.handleTouchStart
      );


      this.section.removeEventListener(
        "touchend",
        this.handleTouchEnd
      );


      this.section.removeEventListener(
        "touchcancel",
        this.handleTouchCancel
      );


      window.removeEventListener(
        "resize",
        this.handleResize
      );


      document.removeEventListener(
        "click",
        this.handleDocumentClick
      );


      this.closeProductBadges();

    }

  }


  /* =========================================================
     INIT
  ========================================================= */

  const initRcreaRetailHeroes =
    () => {

      document
        .querySelectorAll(
          "[data-rcrea-retail-hero]"
        )
        .forEach(
          (section) => {

            if (
              section.dataset
                .rcreaInitialized ===
              "true"
            ) {

              return;

            }


            section.dataset
              .rcreaInitialized =
              "true";


            section.rcreaRetailHero =
              new RcreaRetailHero(
                section
              );

          }
        );

    };


  document.addEventListener(
    "DOMContentLoaded",
    initRcreaRetailHeroes
  );


  /* SHOPIFY EDITOR */

  document.addEventListener(
    "shopify:section:load",
    (event) => {

      const section =
        event.target.querySelector(
          "[data-rcrea-retail-hero]"
        );


      if (
        section &&
        section.dataset
          .rcreaInitialized !==
          "true"
      ) {

        section.dataset
          .rcreaInitialized =
          "true";


        section.rcreaRetailHero =
          new RcreaRetailHero(
            section
          );

      }

    }
  );


  document.addEventListener(
    "shopify:section:unload",
    (event) => {

      const section =
        event.target.querySelector(
          "[data-rcrea-retail-hero]"
        );


      if (
        section &&
        section.rcreaRetailHero
      ) {

        section
          .rcreaRetailHero
          .destroy();


        delete section
          .rcreaRetailHero;


        delete section.dataset
          .rcreaInitialized;

      }

    }
  );


  document.addEventListener(
    "shopify:block:select",
    () => {

      document
        .querySelectorAll(
          "[data-rcrea-retail-hero]"
        )
        .forEach(
          (section) => {

            section
              .rcreaRetailHero
              ?.scheduleMobileLayout();

          }
        );

    }
  );

})();